/* eslint-disable @typescript-eslint/no-explicit-any */

import * as yup from "yup";

import { CURRENCY_PATTERN, EMAIL_PATTERN, PHONE_PATTERN } from "@/utils";

import {
  address,
  averageBill,
  contactDetails,
  customerContact,
  customerType,
  existingSolar,
  largeAppliances,
  ratResultVerify,
  roofMaterial,
  softQuote,
  solarGeneration,
  systemType,
  threePhase,
  usagePattern,
} from "./content.json";
import { transformCurrency } from "./utils/transformCurrency";

const schema = yup.object().shape({
  fullName: yup
    .string()
    .required(contactDetails.fullName.errorMessage)
    .test(
      "is-full-name",
      contactDetails.fullName.formatErrorMessage,
      (value: any) => value && value.trim().split(/\s+/).length >= 2
    ),
  email: yup
    .string()
    .matches(EMAIL_PATTERN, { message: contactDetails.email.errorMessage }),
  contactNumber: yup.string().matches(PHONE_PATTERN, {
    message: customerContact.contactNumber.errorMessage,
  }),
  customerType: yup.string().required(customerType.errorMessage),
  systemType: yup.string().required(systemType.errorMessage),
  averageBill: yup
    .string()
    .transform(transformCurrency)
    .matches(CURRENCY_PATTERN, {
      message: averageBill.input.errorMessage,
    }),
  billDuration: yup.string().required(averageBill.duration.errorMessage),
  threePhase: yup.string().required(threePhase.errorMessage),
  existingSolarSystemSize: yup.string().required(existingSolar.errorMessage),
  existingSolar: yup.string().required(existingSolar.errorMessage),
  roofMaterial: yup.string().required(roofMaterial.errorMessage),
  largeAppliances: yup.array(yup.string()).min(0, largeAppliances.errorMessage),
  usagePattern: yup.string().required(usagePattern.errorMessage),

  // Solar Battery Questions
  winterGeneration: yup
    .number()
    .nullable(true as any)
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .typeError(solarGeneration.winterGenerationInput.errorMessage),
  summerGeneration: yup
    .number()
    .nullable(true as any)
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .typeError(solarGeneration.summerGenerationInput.errorMessage),
  billFrequency: yup.string(),
  selectedServiceProviders: yup
    .array(
      yup.object().shape({
        id: yup.string(),
        name: yup.string(),
      })
    )
    .min(1, softQuote.required),

  // Represent address
  address: yup.string(),
  addressDetails: yup
    .object()
    .shape({
      unitType: yup.object().shape({
        label: yup.string().nullable(),
        value: yup.string().nullable(),
      }),
      unitNumber: yup.string().when("unitType", {
        is: (value: any) => value && value.value,
        then: () => yup.string().required(),
        otherwise: () => yup.string().nullable(),
      }),
      streetNumber: yup.string().required(),
      streetName: yup.string().required(),
      city: yup.string().required(),
      state: yup.string().required(),
      postcode: yup.string().required(),
      streetType: yup.string(),
    })
    .required(address.message.required),

  // Roof Details from Accurassi
  accurassiRoofArea: yup.number(),
  accurassiStoreys: yup.number(),
  accurassiHasSolarPanel: yup.boolean(),

  // Roof Details input by user
  userStoreys: yup
    .number()
    .typeError(ratResultVerify.storeys.input.message.required)
    .min(1, ratResultVerify.storeys.input.message.min)
    .max(108, ratResultVerify.storeys.input.message.max) // Refer to Australia 108 in Melbourne
    .required(ratResultVerify.storeys.input.message.required),
  userRoofPitchValue: yup.number(),

  // Can service the input address
  canService: yup.boolean().optional().required(address.message.required),

  // Editing RAT results
  isEditingResults: yup.boolean(),

  // Represent hard stop
  hardStop: yup.string(),

  // Represent Roof Analysis Result
  roofAnalysisResult: yup.object(),
  roofImageBase64: yup.string(),

  // Recommendation System Size
  selectedSystemSize: yup.number().required(),

  // Fetched Service Provider Details
  fetchedServiceProviderDetails: yup.array(yup.object()),

  // Confirmation page
  didFinishSolarForm: yup.boolean(),
  degradedExperience: yup.boolean(),
  isKipAndGoInstaller: yup.boolean(),
});

const defaultValue = {
  siteReadiness: "",
  buildingType: "",
  fullName: "",
  firstName: "",
  lastName: "",
  contactNumber: "",
  email: "",
  customerType: "",
  systemType: "solar",
  averageBill: "",
  billDuration: undefined,
  threePhase: "",
  existingSolar: "",
  roofMaterial: "",
  timeFrame: "",
  largeAppliances: [],
  usagePattern: "",
  financeType: "",
  userStoreys: undefined,
  userRoofPitchValue: 0,
  isEditingResults: false,
  fetchedServiceProviderDetails: [],
  selectedServiceProviders: [],
  contactTimePreference: [],
  hardStop: "",
  selectedSystemSize: 6,
  didFinishSolarForm: false,
  existingSolarSystemSize: "",
  degradedExperience: false,
  isKipAndGoInstaller: false,
  addressDetails: "",
  address: "",
};

export const SolarForm = { schema, defaultValue };
export * from "./navigation";
